import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArchiveIcon from "@mui/icons-material/Archive";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import Badge from "@mui/material/Badge";
import Cookies from "js-cookie";
import React, { useContext, useMemo } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useAlerts } from "../../../hooks/react-query/alerts/useAlerts";
import { ProfilePicture } from "../../../pages/UserProfile/common/ProfilePicture";
import { CEDAR_SHELL_NAV_ITEMS } from "./cedarShellNavConfig";
import { CedarShellSidenavContext } from "./CedarShellSidenavContext";

// Same "views cookie contains admin, or annotation_admin implies admin" check already
// duplicated independently in MenuItems.tsx and NewAdminPage.tsx - kept local here too rather
// than introducing a shared helper for a single new caller.
const useHasAdminAccess = (): boolean => {
  return useMemo(() => {
    const viewsCookie = Cookies.get("views");
    if (!viewsCookie) return false;
    try {
      const views: string[] = JSON.parse(viewsCookie);
      return views.includes("admin") || views.includes("annotation_admin");
    } catch {
      return false;
    }
  }, []);
};

export const CedarShellSidenav = () => {
  const { collapsed, setCollapsed } = useContext(CedarShellSidenavContext);
  const history = useHistory();
  const location = useLocation();
  const hasAdminAccess = useHasAdminAccess();
  const { alerts } = useAlerts();
  const userDetails = useSelector((state: RootStateOrAny) => state.user.details);

  const { first_name: firstName, last_name: lastName, email, role } = userDetails ?? {};
  const displayName = firstName && lastName ? `${firstName} ${lastName}` : email || "User";

  const navigate = (path: string) => history.push({ pathname: path, search: "" });

  return (
    <nav className={`cedar_shell_sidenav ${collapsed ? "collapsed" : ""}`} data-test-id="cedar-shell-sidenav">
      <button
        className="cedar_shell_sidenav_collapse_toggle"
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        data-test-id="cedar-shell-sidenav-collapse-toggle"
      >
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </button>

      <ul className="cedar_shell_sidenav_items">
        {CEDAR_SHELL_NAV_ITEMS.map(({ label, path, Icon }) => (
          <li
            key={path}
            // CEDAR AI's own gradient row (torch red top-left to black bottom-right) covers the whole
            // pill, not just its icon - same accent as its page header icon and chat avatar.
            className={[location.pathname === path ? "active" : "", label === "CEDAR AI" ? "cedar_shell_sidenav_gradient_item" : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => navigate(path)}
            data-test-id={`cedar-shell-nav-${label.toLowerCase()}`}
          >
            {label === "Alerts" && alerts.length > 0 ? (
              <Badge badgeContent={alerts.length} className="cedar_shell_sidenav_alert_badge" data-test-id="cedar-shell-alert-badge">
                <Icon />
              </Badge>
            ) : (
              <Icon />
            )}
            {!collapsed && <span>{label}</span>}
          </li>
        ))}

        {hasAdminAccess && (
          <li className="cedar_shell_sidenav_subsection">
            {!collapsed && <span className="cedar_shell_sidenav_subsection_label">Admin</span>}
            <ul>
              <li
                className={location.pathname === "/archived" ? "active" : ""}
                onClick={() => navigate("/archived")}
                data-test-id="cedar-shell-nav-archived"
              >
                <ArchiveIcon />
                {!collapsed && <span>Archived</span>}
              </li>
              <li
                className={location.pathname === "/admin" ? "active" : ""}
                onClick={() => navigate("/admin")}
                data-test-id="cedar-shell-nav-admin"
              >
                <AdminPanelSettingsIcon />
                {!collapsed && <span>Admin</span>}
              </li>
            </ul>
          </li>
        )}
      </ul>

      <div
        className="cedar_shell_sidenav_profile"
        onClick={collapsed ? () => setCollapsed(false) : undefined}
        data-test-id="cedar-shell-sidenav-profile"
      >
        <ProfilePicture firstname={firstName || email || " "} lastname={lastName || " "} />
        {!collapsed && (
          <div className="cedar_shell_sidenav_profile_details">
            <p className="cedar_shell_sidenav_profile_name">{displayName}</p>
            {role && <p className="cedar_shell_sidenav_profile_role">{role}</p>}
          </div>
        )}
        {!collapsed && (
          <button
            className="cedar_shell_sidenav_logout"
            onClick={() => navigate("/logout")}
            aria-label="Log out"
            data-test-id="cedar-shell-logout"
          >
            <LogoutIcon />
          </button>
        )}
      </div>
    </nav>
  );
};
